import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as queueApi from "../../api/queueApi";
import { failureMessage } from "../../api/client";
import { clearSession, setSessionCode } from "../../actions/sessionActions";
import { SESSION_CODE_LENGTH, normalizeSessionCode } from "../../config";
import { hasSessionCache } from "../../offline/sessionCache";

/** The guest's Join tab: type a code, or the other ways in; also the way out of the current session. */
const JoinPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const current = useSelector((state) => state.session.Code);
  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const join = async (value) => {
    const sessionCode = normalizeSessionCode(value);
    if (sessionCode.length !== SESSION_CODE_LENGTH || busy) return;
    setBusy(true);
    setError(null);
    try {
      await queueApi.getQueue(sessionCode);
      dispatch(setSessionCode(sessionCode));
      navigate("/lyrics", { replace: true });
    } catch (err) {
      if (!err.response && hasSessionCache(sessionCode)) {
        dispatch(setSessionCode(sessionCode));
        navigate("/lyrics", { replace: true });
        return;
      }
      setError(err.response?.status === 404 ? `No session with code ${sessionCode}` : failureMessage(err, "Could not reach the server, please try again"));
    } finally {
      setBusy(false);
    }
  };

  const onChange = (event) => {
    const value = normalizeSessionCode(event.target.value).slice(0, SESSION_CODE_LENGTH);
    setCode(value);
    setError(null);
    if (value.length === SESSION_CODE_LENGTH) join(value);
  };

  const leave = () => {
    dispatch(clearSession());
    navigate("/", { replace: true });
  };

  const boxes = Array.from({ length: SESSION_CODE_LENGTH }, (_, index) => code[index] || "");

  return (
    <div className="page" data-screen-label="Join">
      <div className="page-head">
        <h2 className="page-title">Join a session</h2>
        <p className="page-sub">Three ways in. Any of them works.</p>
      </div>
      <form
        className="card"
        onSubmit={(event) => {
          event.preventDefault();
          join(code);
        }}
      >
        <div className="kicker kicker-lg" style={{ marginBottom: 12 }}>
          Type the code
        </div>
        <label className="code-entry">
          <span className="sr-only">Session code</span>
          <div className="code-boxes" aria-hidden="true">
            {boxes.map((character, index) => (
              <div key={index} className={`code-box${character ? " filled" : ""}${index === code.length ? " active" : ""}`}>
                {character}
              </div>
            ))}
          </div>
          <input aria-label="Session code" value={code} onChange={onChange} maxLength={SESSION_CODE_LENGTH} autoComplete="off" autoCapitalize="characters" spellCheck={false} disabled={busy} />
        </label>
        {error && (
          <p className="error-text" role="alert" style={{ marginTop: 10 }}>
            {error}
          </p>
        )}
      </form>
      <div className="card">
        <div className="between">
          <span className="grow">
            <span className="card-title">Scan the QR</span>
            <span className="row-sub">Point your camera app at the code on the screen or the printed sheet; it opens the session.</span>
          </span>
          <span className="btn btn-round btn-md btn-outline" aria-hidden="true">
            ▣
          </span>
        </div>
      </div>
      <div className="card-dashed note">A shared link drops you straight in — no account needed to sing along.</div>
      {current && (
        <>
          <div style={{ flex: 1 }} />
          <div className="note">You are in session {current}.</div>
          <button type="button" className="btn btn-outline" onClick={leave}>
            Leave this session
          </button>
        </>
      )}
    </div>
  );
};

export default JoinPage;
